<script setup lang="ts">
import { toRef, computed } from 'vue';
import { CdrText, CdrLink } from '@rei/cedar';
import type { ContactUsSectionModel } from '../../api/interfaces/contactUsSectionModel';
import type { PhoneNumbers } from './composables/usePhoneInteraction';
import { usePhoneInteraction } from './composables/usePhoneInteraction';

interface ContactUsTextSectionProps {
  section: ContactUsSectionModel;
  helpCenterNumbers: PhoneNumbers;
}

const props = defineProps<ContactUsTextSectionProps>();

const emit = defineEmits<{
  sectionClick: [section: ContactUsSectionModel, phoneNumber?: { tel: string }];
}>();

const helpCenterNumbersRef = toRef(() => props.helpCenterNumbers);
const { formatPhoneNumbers } = usePhoneInteraction(helpCenterNumbersRef);

// handle section click
const handleSectionClick = (phoneNumber?: { tel: string }) => {
  if (phoneNumber) {
    emit('sectionClick', props.section, phoneNumber);
  } else {
    emit('sectionClick', props.section);
  }
};

// process subText to replace section name with links when redirectUrl is available
const processSubText = (section: ContactUsSectionModel) => {
  if (!section.subText || !section.redirectUrl || !section.section) {
    return '';
  }

  // split text by the section name and return parts, should be case insensitive
  const sectionNameRegex = new RegExp(`(\\b${section.section}\\b)`, 'gi');
  const parts = section.subText.split(sectionNameRegex);
  return parts;
};

// simplified content processing
const processedContent = computed(() => {
  const {
    section: sectionName, redirectUrl, subText, buttonContent,
  } = props.section;

  // call us sections need phone handling
  if (sectionName === 'Call Us') return { type: 'callUs' };

  // no links without redirect url
  if (!redirectUrl) return { type: 'plain', text: subText };

  // store sections: full text clickable
  if (sectionName?.toLowerCase().includes('store')) {
    return { type: 'fullTextLink', text: subText, redirectUrl };
  }

  // button content: smart linking
  if (buttonContent && subText?.toLowerCase().includes(buttonContent.toLowerCase())) {
    const regex = new RegExp(`(\\b${buttonContent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b)`, 'gi');
    return {
      type: 'embedded', parts: subText.split(regex), buttonText: buttonContent, redirectUrl,
    };
  }

  // button content: append to text
  if (buttonContent) {
    return {
      type: 'appended', text: subText, buttonText: buttonContent, redirectUrl,
    };
  }

  // section name linking
  if (subText?.toLowerCase().includes(sectionName.toLowerCase())) {
    return {
      type: 'sectionLink', parts: processSubText(props.section), sectionName, redirectUrl,
    };
  }

  // fallback
  return { type: 'plain', text: subText };
});

// helper to determine if a text part should be linkable
const isLinkablePart = (part: string, content: any) => {
  if (content.type === 'embedded') {
    return part.toLowerCase() === content.buttonText.toLowerCase();
  }
  if (content.type === 'sectionLink') {
    return part.toLowerCase() === content.sectionName.toLowerCase();
  }
  return false;
};
</script>

<template>
  <div class="contact-us-text-section">
    <CdrText
      tag="h3"
      tabindex="0"
    >
      <span class="contact-us-footer-title">
        {{ section.section }}:
      </span>
    </CdrText>
    <CdrText tabindex="0">
      <span class="contact-us-sec-body-footer">
        <!-- Call Us Section -->
        <template v-if="processedContent.type === 'callUs'">
          <div>{{ section.subText }}</div>
          <div class="phone-entries">
            <div
              v-for="(entry, index) in formatPhoneNumbers(section.sideText)"
              :key="index"
              class="phone-entry"
            >
              <template v-if="entry.type === 'us' || entry.type === 'international'">
                {{ entry.label }}
                <CdrLink
                  :href="entry.telUrl"
                  modifier="standalone"
                  class="phone-link"
                >
                  {{ entry.phoneNumber }}
                </CdrLink>
              </template>
              <template v-else>
                {{ entry.text }}
              </template>
            </div>
          </div>
        </template>

        <!-- full text link -->
        <template v-else-if="processedContent.type === 'fullTextLink'">
          <CdrLink
            :href="processedContent.redirectUrl"
            modifier="standalone"
            class="section-link"
            @click.prevent="() => handleSectionClick(null)"
          >
            {{ processedContent.text }}
          </CdrLink>
        </template>

        <!-- embedded links (button or section name) -->
        <template v-else-if="processedContent.type === 'embedded' || processedContent.type === 'sectionLink'">
          <span
            v-for="(part, index) in processedContent.parts"
            :key="`link-${index}`"
          >
            <CdrLink
              v-if="isLinkablePart(part, processedContent)"
              :href="processedContent.redirectUrl"
              modifier="standalone"
              class="section-link"
              @click.prevent="() => handleSectionClick(null)"
            >
              {{ part }}
            </CdrLink>
            <template v-else>{{ part }}</template>
          </span>
        </template>

        <!-- appended buttonContent -->
        <template v-else-if="processedContent.type === 'appended'">
          <span>{{ processedContent.text }}&nbsp;</span>
          <CdrLink
            :href="processedContent.redirectUrl"
            modifier="standalone"
            class="section-link"
            @click.prevent="() => handleSectionClick(null)"
          >
            {{ processedContent.buttonText }}
          </CdrLink>
        </template>

        <!-- plain text -->
        <template v-else>
          {{ processedContent.text }}
        </template>
      </span>
    </CdrText>
  </div>
</template>

<style lang="scss">
  @import 'src/main/style/contactUs.scss';

  .content-with-appended-cta {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .appended-cta-link {
    align-self: flex-start;
    font-weight: 600;
    margin-top: 0.25rem;
  }
</style>
